import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { CodeSystemVersionListModel } from '../models/CodeSystemVersionListModel';
import { CodeSystemVersionDashboardModel } from '../models/CodeSystemVersionDashboardModel';
import { CodeSystemVersionSimpleExtractModel } from '../models/CodeSystemVersionSimpleExtractModel';
import { TermModel } from '../models/TermModel';
import { PropertyModel } from '../models/PropertyModel';
import { RelationshipModel } from '../models/RelationshipModel';
import { ConceptWorkspaceModel } from '../models/ConceptWorkspaceModel';
import { ValueSetModel } from '../models/ValueSetModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { ModelResponse } from '../../core/services/ModelResponse';
import { AddConceptVersionModel } from '../models/AddConceptVersionModel';
import { DeleteConceptVersionModel } from '../models/DeleteConceptVersionModel';
import { UndoDeleteConceptVersionModel } from '../models/UndoDeleteConceptVersionModel';
import { LookupModel } from '../../common/models/LookupModel';
import { AddCodeSystemModel } from '../models/AddCodeSystemModel';
import { AddValueSetModel } from '../models/AddValueSetModel';
import { RelateConceptVersionModel } from '../models/RelateConceptVersionModel';
import { DownloadFileService } from '../../common/utilities/DownloadFileService';

@Injectable()
export class VocabularyService {
  constructor(public httpClient: HttpClient, private downloadFileService: DownloadFileService) {}

  getCodeSystemVersionList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string, loadLatest: boolean): Observable<QueryResponse<CodeSystemVersionListModel>> {
    var params = new HttpParams().set('skip', (page * pageSize).toString()).set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort).set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (loadLatest) {
      params = params.set('loadLatest', loadLatest.toString());
    }
    return this.httpClient.get<QueryResponse<CodeSystemVersionListModel>>('/api/VocabularyModule/Vocabulary/ListQuery', { params: params });
  }

  getValueSets(key: string, searchTerm: string, hideEmpty: boolean): Observable<ModelResponse<CodeSystemVersionDashboardModel>> {
    var params = new HttpParams().set('key', key).set('hideEmpty', hideEmpty ? 'true' : 'false');
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<ModelResponse<CodeSystemVersionDashboardModel>>('/api/VocabularyModule/Vocabulary/GetValueSets', { params: params });
  }

  getConceptsForValueSet(page: number, pageSize: number, sort: string, sortDirection: string, key: string, version: string, searchTerm: string): Observable<QueryResponse<ValueSetModel>> {
    var params = new HttpParams()
      .set('skip', (page * pageSize).toString())
      .set('take', pageSize.toString())
      .set('version', version)
      .set('key', key);
    if (sort) {
      params = params.set('sort', sort).set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<ValueSetModel>>('/api/VocabularyModule/Vocabulary/GetConceptsForValueSet', { params: params });
  }

  getSelectedConcept(key: string, code: string): Observable<ModelResponse<ConceptWorkspaceModel>> {
    var params = new HttpParams().set('key', key).set('code', code);
    return this.httpClient.get<ModelResponse<ConceptWorkspaceModel>>('/api/VocabularyModule/Vocabulary/GetSelectedConcept/', { params: params });
  }

  addConcept(addModel: AddConceptVersionModel): Observable<ModelResponse<AddConceptVersionModel>> {
    return this.httpClient.put<ModelResponse<AddConceptVersionModel>>('/api/VocabularyModule/Vocabulary/Add/', addModel);
  }

  addTerm(key: string, model: TermModel): Observable<ModelResponse<TermModel>> {
    return this.httpClient.put<ModelResponse<TermModel>>(`/api/VocabularyModule/Vocabulary/${key}/Term/`, model);
  }

  reviseTerm(key: string, model: TermModel): Observable<ModelResponse<TermModel>> {
    return this.httpClient.post<ModelResponse<TermModel>>(`/api/VocabularyModule/Vocabulary/${key}/Term/${model.key}`, model);
  }

  removeTerm(key: string, termKey: string): Observable<ModelResponse<TermModel>> {
    return this.httpClient.delete<ModelResponse<TermModel>>(`/api/VocabularyModule/Vocabulary/${key}/Term/${termKey}`);
  }

  addProperty(key: string, model: PropertyModel): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.put<ModelResponse<PropertyModel>>(`/api/VocabularyModule/Vocabulary/${key}/Property/`, model);
  }

  reviseProperty(key: string, model: PropertyModel): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.post<ModelResponse<PropertyModel>>(`/api/VocabularyModule/Vocabulary/${key}/Property/${model.key}`, model);
  }

  removeProperty(key: string, propertyKey: string): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.delete<ModelResponse<PropertyModel>>(`/api/VocabularyModule/Vocabulary/${key}/Property/${propertyKey}`);
  }

  addRelationship(key: string, model: RelationshipModel): Observable<ModelResponse<RelationshipModel>> {
    return this.httpClient.put<ModelResponse<RelationshipModel>>(`/api/VocabularyModule/Vocabulary/${key}/Relationship/`, model);
  }

  removeRelationship(key: string, relationshipKey: string): Observable<ModelResponse<RelationshipModel>> {
    return this.httpClient.delete<ModelResponse<RelationshipModel>>(`/api/VocabularyModule/Vocabulary/${key}/Relationship/${relationshipKey}`);
  }

  createDraft(key: string): Observable<ModelResponse<string>> {
    return this.httpClient.put<ModelResponse<string>>(`/api/VocabularyModule/Vocabulary/${key}/CreateDraft/`, {});
  }

  finalizeDraft(key: string): Observable<ModelResponse<boolean>> {
    return this.httpClient.post<ModelResponse<boolean>>(`/api/VocabularyModule/Vocabulary/${key}/FinalizeDraft/`, {});
  }

  clearCache(): Observable<ModelResponse<boolean>> {
    return this.httpClient.post<ModelResponse<boolean>>(`/api/VocabularyModule/Vocabulary/ClearCache/`, {});
  }

  getRelationshipTypes(key: string): Observable<LookupModel[]> {
    var params = new HttpParams().set('key', key);
    return this.httpClient.get<QueryResponse<LookupModel>>('/api/VocabularyModule/Vocabulary/GetRelationshipTypes', { params: params }).map((res) => {
      var results = res.items;
      return results;
    });
  }

  getConceptsForRelationships(key: string, valueSetCode: string, codeSystemVersionKey: string, searchTerm: string): Observable<LookupModel[]> {
    var params = new HttpParams().set('key', key).set('valueSetCode', valueSetCode);
    if (codeSystemVersionKey) {
      params = params.set('codeSystemVersionKey', codeSystemVersionKey);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<LookupModel>>('/api/VocabularyModule/Vocabulary/GetConceptsForRelationships', { params: params }).map((res) => {
      var results = res.items;
      return results;
    });
  }

  getSubmittingEntitiesWithoutCodeSystem(searchTerm: string): Observable<LookupModel[]> {
    var params = new HttpParams();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<LookupModel>>('/api/VocabularyModule/Vocabulary/GetSubmittingEntitiesWithoutCodeSystem', { params: params }).map((res) => {
      var results = res.items;
      return results;
    });
  }

  getCodeSystemsVersionsForRelationships(key: string, searchTerm: string): Observable<LookupModel[]> {
    var params = new HttpParams().set('key', key);
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<LookupModel>>('/api/VocabularyModule/Vocabulary/GetCodeSystemsVersionsForRelationships', { params: params }).map((res) => {
      var results = res.items;
      return results;
    });
  }

  addCodeSystem(addModel: AddCodeSystemModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>('/api/VocabularyModule/Vocabulary/AddCodeSystem/', addModel);
  }

  deleteDraft(key: string): Observable<ModelResponse<boolean>> {
    return this.httpClient.post<ModelResponse<boolean>>(`/api/VocabularyModule/Vocabulary/${key}/DeleteDraft/`, {});
  }

  deleteConcept(deleteModel: DeleteConceptVersionModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>('/api/VocabularyModule/Vocabulary/Delete/', deleteModel);
  }

  undoDeleteConcept(undoDeleteModel: UndoDeleteConceptVersionModel): Observable<ModelResponse<UndoDeleteConceptVersionModel>> {
    return this.httpClient.post<ModelResponse<UndoDeleteConceptVersionModel>>('/api/VocabularyModule/Vocabulary/UndoDelete/', undoDeleteModel);
  }

  addValueSet(addModel: AddValueSetModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>('/api/VocabularyModule/Vocabulary/AddValueSet/', addModel);
  }

  relateConceptToValueSet(relateModel: RelateConceptVersionModel): Observable<ModelResponse<RelateConceptVersionModel>> {
    return this.httpClient.put<ModelResponse<RelateConceptVersionModel>>('/api/VocabularyModule/Vocabulary/Relate/', relateModel);
  }

  checkIfMappedCodeSystem(codeSystemVersionKey: string): Observable<ModelResponse<boolean>> {
    return this.httpClient.get<ModelResponse<boolean>>(`/api/VocabularyModule/Vocabulary/CheckIfMappedCodeSystem/${codeSystemVersionKey}`, {});
  }

  exportCodeSystemVersion(type: string, model: CodeSystemVersionListModel): Observable<ModelResponse<CodeSystemVersionListModel>> {
    model.projectName = 'Fasams';
    return this.httpClient.post<ModelResponse<CodeSystemVersionListModel>>(`/api/VocabularyModule/Vocabulary/ExportUrl/${type}`, model).switchMap((response) => {
      return this.httpClient.post<Blob>(response.model.url, model, { responseType: 'blob' as 'json', observe: 'response' }).map((r) => {
        this.downloadFileService.export(r, model);
        var response = new ModelResponse<CodeSystemVersionListModel>();
        response.model = model;
        return response;
      });
    });
  }

  openExtract(url: string, model: any): void {
    this.downloadFileService.openExtract(url, model);
  }
}



// WEBPACK FOOTER //
// ./src/app/vocabulary/services/VocabularyService.ts